import React from 'react';
import { colors } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import { KeyboardBackspace } from '@material-ui/icons';
import ArrowBackIosRoundedIcon from '@material-ui/icons/ArrowBackIosRounded';
import { makeStyles } from '@material-ui/styles';
import { type LaunchDarklyFlags } from '@/common/launchDarkly';
import { useSearchParams } from '@/hooks/use-search-params';
import { logoutCleanup } from '../../auth';
import { useSureYouWantToLeaveDialog } from '../../components/senderBuild/footer-controls/use-sure-you-want-to-leave-dialog';
import { useNavigatedFromForms } from '../../hooks/use-navigated-from-forms';
import { EnvelopeStatuses } from '../../lib/constants';
import { useIsMediumScreenMediaQuery } from '../../lib/isSmallScreen';
import { useShallowSelector } from '../../lib/reduxHooks';
import { FormsBackPage } from '../../store/senderBuild/types';
import { withFrameOrigin } from '../../utils/frame-origin';
import { shouldUseFormsRedirect } from './navigation-utils';

const useStyles = makeStyles(
  () => ({
    backButtonWrapper: {
      display: 'inline-block',
      flex: 'auto',
    },
    backButtonWrapperMobile: {
      display: 'inherit',
      flex: 'inherit',
    },
    mobileExitButton: {
      height: '40px',
      width: '40px',
      color: colors.blue[800],
      border: `2px solid ${colors.blue[800]}`,
    },
  }),
  { name: 'BuilderExitBackButtons' }
);

export function BuilderExitBackButtons(props: { flags: LaunchDarklyFlags }) {
  const { flags } = props;
  const { saving, config, envelopeStatus } = useShallowSelector((state) => ({
    saving: state.senderBuild.saving,
    isAutoSaving: state.senderBuild.isAutoSaving,
    config: state.senderBuild.config,
    envelopeStatus: state.senderBuild.status,
  }));

  const { theme = 'default' } = useSearchParams();

  const classes = useStyles();
  const isMdScreenOrSmaller = useIsMediumScreenMediaQuery();

  const shouldUseFormsNavigation = useNavigatedFromForms();
  const sureYouWantToLeave = useSureYouWantToLeaveDialog({ flags });

  function handleBackButtonClick() {
    if (!shouldUseFormsNavigation) {
      window.history.back();
    } else {
      sureYouWantToLeave.onNavigateAwayFromPage();
      handleExitBackToForms();
    }
  }

  function handleExitBackToForms() {
    logoutCleanup();

    const redirectUrl = config?.senderUi?.sendRedirectUrl;
    if (!shouldUseFormsRedirect(redirectUrl)) {
      window.history.back();
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const backPage = params.get('backPage');
    const formsRedirectUrl = new URL(redirectUrl);
    const formsEnvelopeId = formsRedirectUrl.searchParams.get('sentEnvelopeId');

    if (!backPage || !formsEnvelopeId) {
      window.history.back();
      return;
    }

    switch (backPage) {
      case FormsBackPage.Envelopes:
        window.location.href = withFrameOrigin(
          `${formsRedirectUrl.origin}${formsRedirectUrl.pathname}?revertedEnvelopeId=${formsEnvelopeId}`
        );
        break;
      case FormsBackPage.Fill:
        window.location.href = withFrameOrigin(
          `${formsRedirectUrl.origin}/envelopes/${formsEnvelopeId}/fill?revertedEnvelopeId=${formsEnvelopeId}&theme=${theme}`
        );
        break;
      default:
        window.history.back();
    }
  }

  const backButtonClass = isMdScreenOrSmaller
    ? `${classes.backButtonWrapper} ${classes.backButtonWrapperMobile}`
    : classes.backButtonWrapper;

  function renderBackButtons() {
    if (!shouldUseFormsNavigation) {
      return (
        <div className={backButtonClass}>
          <Button
            data-spec="backButton"
            color="primary"
            variant={isMdScreenOrSmaller ? 'outlined' : undefined}
            onClick={() => sureYouWantToLeave.showModal(handleBackButtonClick)}
            startIcon={isMdScreenOrSmaller ? undefined : <KeyboardBackspace />}
            className="rounded-full hover:bg-blue-50 max-md:w-36 py-3 px-6"
          >
            {isMdScreenOrSmaller ? 'Exit' : 'Back'}
          </Button>
        </div>
      );
    }

    if (isMdScreenOrSmaller) {
      return (
        <IconButton
          onClick={() => sureYouWantToLeave.showModal(handleBackButtonClick)}
          className={classes.mobileExitButton}
        >
          <ArrowBackIosRoundedIcon />
        </IconButton>
      );
    }

    if (envelopeStatus !== EnvelopeStatuses.CORRECTING) {
      return (
        <div className={backButtonClass}>
          <Button
            data-spec="backButton"
            className="hover:bg-blue-50"
            color="primary"
            disabled={saving}
            variant={isMdScreenOrSmaller ? 'outlined' : undefined}
            onClick={() => sureYouWantToLeave.showModal(handleBackButtonClick)}
            style={{ width: isMdScreenOrSmaller ? '140px' : '' }}
            startIcon={isMdScreenOrSmaller ? undefined : <KeyboardBackspace />}
          >
            Back
          </Button>
        </div>
      );
    }
  }

  return (
    <>
      {renderBackButtons()}
      {sureYouWantToLeave.renderDialog()}
    </>
  );
}
